import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt' // LAP-CP03
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux' // LAP-CP04
import { loadWallet } from '../../../Components/Billing/billingThunks' // LAP-CP04
import { makeGetRequest, makePostRequest } from 'utils/api' // LAP-CP06/07
import urls from 'utils/urls' // LAP-CP06

import WTypography from '../../../Components/whiteTypography'
import ScorePatrimonial from './ScorePatrimonial'
import DiagnosticPanel from './DiagnosticPanel'
import ActionsPrioritaires from './ActionsPrioritaires'
import ProjetsCreables from './ProjetsCreables'
import ConseillerChat from './ConseillerChat'
import SimulationCompare from './SimulationCompare'

// LAP-748 — Plus aucun import depuis mockPatrimoineData pour les données
// affichées. Tout passe désormais par buildAdvisorFromReports qui calcule
// score, diagnostic, actions, projets et projection à partir des VRAIES
// données utilisateur (Redux + MonBilanPatrimonialReports).
import { buildInitialChatMessages } from '../data/mockPatrimoineData'
import { buildAdvisorFromReports } from '../utils/buildAdvisorFromReports'

/**
 * ConseillerPatrimonialPage
 * --------------------------
 * Page principale "Mon conseiller patrimonial IA".
 * Accessible depuis la carte "Conseils et Astuces" du dashboard.
 *
 * Route câblée dans App.js : /conseiller-patrimonial
 */
// LAP-748 — Style des CTAs du bandeau "Affinez votre analyse" (factorisé)
const ctaButtonSx = {
  bgcolor: 'rgba(126,156,219,0.18)',
  color: '#a8c1f0',
  textTransform: 'none',
  '&:hover': { bgcolor: 'rgba(126,156,219,0.3)' },
}

function ConseillerPatrimonialPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch() // LAP-CP04
  const { t, i18n } = useTranslation('layout')
  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [regenerating, setRegenerating] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [wasFromCache, setWasFromCache] = useState(null)
  // LAP-748 — state pour les VRAIES données utilisateur (plus de mock)
  // LAP-i18n — On garde maintenant `reports` et `realUser` séparément pour
  // pouvoir RE-construire l'advisor à chaque changement de langue (i18n.language)
  // sans refetch. buildAdvisorFromReports utilise `t` pour produire toutes ses
  // chaînes affichées.
  const [reports, setReports] = useState(null)
  const [realUser, setRealUser] = useState(null)
  const advisorData = useMemo(() => {
    if (!reports) return null
    return buildAdvisorFromReports({ reports, user: realUser || {}, t })
  }, [reports, realUser, t, i18n.language])

  const siteUser = useSelector((s) => s.user)
  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  // LAP-CP03 — Scroll au top quand on arrive sur la page. Sans ça, on hérite
  // de la position de scroll de la page précédente (ex: utilisateur scrollé
  // sur /MonPatrimoine puis qui clique sur la card en bas → arrive au milieu).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  // LAP-CP06 — Fetch initial de l'analyse patrimoniale.
  //
  // CONTRAT BACKEND attendu :
  //   GET  /api/general/patrimoine/advisor-data/
  //     → idempotent. Si l'utilisateur a déjà une analyse en cache
  //       (table `patrimoine_advisor_cache`), renvoie le cache (gratuit).
  //       Si pas de cache → génère via LLM (consomme 1 crédit) + persiste
  //       + renvoie. Réponse : { score, diagnostic, actions, projets,
  //       projection, generated_at, was_freshly_generated }
  //
  //   POST /api/general/patrimoine/advisor-data/regenerate/
  //     → force une nouvelle génération (consomme 1 crédit) même si cache.
  //       Met à jour le cache + renvoie la nouvelle analyse.
  //
  // Comportement front : on GET au mount → si was_freshly_generated=true,
  // on refresh le wallet (sinon le solde reste inchangé). Tant que les
  // endpoints n'existent pas, on tombe sur le mock localement avec un
  // log console et l'UI reste fonctionnelle pour la démo.
  useEffect(() => {
    fetchAdvisorData({ forceRefresh: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // LAP-748 — Fetch des VRAIES données + computation de l'advisor.
  //
  // Endpoints utilisés (déjà existants côté backend) :
  //   GET /api/user/user/                              → profil utilisateur (nom, prénom, ville, etc.)
  //   GET /api/general/mon-bilan-patrimonial-reports/ → reports (portrait, budget, patrimoine, objectifs, profil investisseur, conseils)
  //
  // Le calcul du score / diagnostic / actions / projets / projection se fait
  // ENTIÈREMENT côté front via buildAdvisorFromReports (cf. utils/buildAdvisorFromReports.js).
  // C'est cohérent et déterministe : chaque user voit SES données.
  //
  // Tiered pricing crédits (sur le refresh) :
  //   - forceRefresh=true → ?refresh=conseil&feature=wealth_advisor_full (5 crédits cible backend)
  //   - forceRefresh=false → GET nu, lit le cache, 0 crédit
  const fetchAdvisorData = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) {
      setRegenerating(true)
    } else {
      setInitialLoading(true)
    }
    try {
      const path = forceRefresh
        ? `${urls.MonBilanPatrimonialReports}?refresh=conseil&feature=wealth_advisor_full`
        : urls.MonBilanPatrimonialReports

      // Fetch reports + user profile en parallèle pour gagner en latence
      const [reportsResp, userResp] = await Promise.all([
        makeGetRequest(path, {}, true),
        makeGetRequest('/api/user/user/', {}, true).catch(() => null),
      ])

      const reportsData = Array.isArray(reportsResp?.data) ? reportsResp.data : []
      const userData = userResp?.data || siteUser?.user || {}

      // LAP-748 / LAP-i18n — On stocke `reports` + `realUser` ; la construction
      // de l'advisor (incluant toutes les chaînes traduites) est faite par le
      // useMemo plus haut, qui réagit aussi aux changements de langue.
      setReports(reportsData)
      setRealUser(userData)

      const freshGen =
        forceRefresh || reportsResp?.data?.was_freshly_generated === true
      setWasFromCache(!freshGen)
      if (reportsResp?.data?.generated_at) {
        setLastUpdate(new Date(reportsResp.data.generated_at))
      } else if (freshGen) {
        setLastUpdate(new Date())
      }

      // Refresh wallet uniquement si génération fresh (crédit consommé)
      if (freshGen) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
      }

      if (forceRefresh) {
        setSnackbar({
          severity: 'success',
          message: t('conseillerPatrimonial.regenerateSuccess'),
        })
      }
    } catch (e) {
      console.warn('[ConseillerPatrimonial] fetch error', e?.message)
      // En cas d'erreur on construit quand même un advisor minimal à partir
      // de ce qu'on a sous la main (Redux user) → pas de mock fantôme.
      if (!reports) {
        setReports([])
        setRealUser(siteUser?.user || {})
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerPatrimonial.regenerateError'),
        })
      }
    } finally {
      if (forceRefresh) {
        setRegenerating(false)
      } else {
        setInitialLoading(false)
      }
    }
  }

  // LAP-CP06 — Régénération de l'analyse patrimoniale complète (5 crédits).
  // Force une nouvelle génération côté backend en passant
  // ?refresh=conseil&feature=wealth_advisor_full. fetchAdvisorData mutualise
  // la logique fetch/cache/refresh et le refresh du wallet.
  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAdvisorData({ forceRefresh: true })
  }

  // LAP-CP07 — Envoi d'un message au Life Agent en chat.
  // CONTRAT BACKEND attendu :
  //   POST /api/general/wealth-advisor/chat/
  //     body: { message, locale, history: [{role, content}, ...] }
  //     output: { reply, source, locale, generated_at }
  //     coût: 1 crédit, feature `wealth_chat`
  //     erreurs: HTTP 402 → modale "Crédits insuffisants" globale prend le relais
  //
  // Tant que l'endpoint n'existe pas, l'API rejette et ConseillerChat
  // utilise sa réponse de démo (setTimeout 1.2s).
  const handleChatSend = async (message, locale) => {
    try {
      // TODO BACKEND : créer la route POST /wealth-advisor/chat/ avec billing
      // feature=wealth_chat (1 crédit/message). Le prompt doit injecter le
      // profil + objectifs + risque + connaissance + intérêts de l'utilisateur
      // pour des réponses pertinentes (cf. TODO INTÉGRATION dans mockPatrimoineData.js).
      const response = await makePostRequest(
        '/api/general/wealth-advisor/chat/',
        { message, locale, feature: 'wealth_chat' },
        {},
        true,
      )
      const data = response?.data || {}
      // Refresh wallet (le message a coûté 1 crédit). Silencieux en cas d'erreur.
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      // Si l'endpoint n'existe pas / erreur réseau, on laisse ConseillerChat
      // utiliser sa réponse de démo en relayant le rejet.
      console.warn('[ConseillerChat] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  // LAP-CP05 — Création de projet depuis une action prioritaire ou une carte
  // projet. AVANT : on navigue vers /mesobjectifs (qui crée un OBJECTIF) bien
  // que le bouton s'appelle "Créer le projet" — incohérent.
  //
  // LAP-fix-projet-vs-objectif :
  //   - "Créer le projet" → /todos?action=new-project (CreateProject de MesToDo,
  //      qui crée une entité Projet avec nom + objectif + deadline + LifeDomain).
  //   - "Créer un objectif" → /mesobjectifs?action=new-from-advisor (flow
  //      existant qui ouvre CreateObjectifsForm + permet de choisir parmi les
  //      objectifs prédéfinis du compte).
  // Le prefill est passé via location.state pour pré-remplir le formulaire cible.
  const navigateToCreateProject = (projet, snackbarMessage) => {
    setSnackbar({ severity: 'success', message: snackbarMessage })
    const projectPrefill = {
      Name: projet?.title || '',
      Objectifs: projet?.description || '',
      DateFinObjectif: '',
      LifeDomain: null,
      // On garde aussi l'autofilled pour la future intégration côté MesToDo.
      autofilled: projet?.autofilled || null,
    }
    setTimeout(() => {
      navigate('/todos?action=new-project', { state: { prefill: projectPrefill } })
    }, 400)
  }

  const navigateToCreateObjectif = (prefill, snackbarMessage) => {
    setSnackbar({ severity: 'success', message: snackbarMessage })
    setTimeout(() => {
      navigate('/mesobjectifs?action=new-from-advisor', { state: { prefill } })
    }, 400)
  }

  const handleCreateProject = (action) => {
    const projet = (advisorData?.projetsSugeres || []).find(
      (p) => p.id === action.projetId,
    )
    if (projet?.autofilled) {
      // Depuis une action, on continue de proposer un projet (l'utilisateur
      // pourra créer un objectif depuis la card projet elle-même).
      navigateToCreateProject(
        projet,
        t('conseillerPatrimonial.projectPrepared', { title: projet.title }),
      )
    } else {
      // Action sans projet associé : on garde l'utilisateur sur la page et on
      // l'informe qu'elle a été ajoutée à son plan (le plan sera persisté
      // côté backend dans une future itération).
      setSnackbar({
        severity: 'info',
        message: t('conseillerPatrimonial.actionAdded', { title: action.title }),
      })
    }
  }

  const handleCreateProjet = (projet) => {
    navigateToCreateProject(
      projet,
      t('conseillerPatrimonial.projectCreating', { title: projet.title }),
    )
  }

  // LAP-fix-projet-vs-objectif — Nouvelle action : créer un OBJECTIF (entité
  // MesObjectifs) à partir de la card de projet recommandé, en s'appuyant sur
  // les objectifs prédéfinis existants.
  const handleCreateObjectif = (projet) => {
    navigateToCreateObjectif(
      projet?.autofilled,
      t('conseillerPatrimonial.objectiveCreating', { title: projet.title }),
    )
  }

  const handleSimulate = (action) => {
    setSnackbar({
      severity: 'info',
      message: t('conseillerPatrimonial.simulationLaunched', { title: action.title }),
    })
  }

  const handleDownloadReport = () => {
    setSnackbar({
      severity: 'info',
      message: t('conseillerPatrimonial.generatingReport'),
    })
  }

  // LAP-CP06 — Splash screen pendant la TOUTE PREMIÈRE génération.
  // À ce stade-là le backend cuit le LLM (peut prendre 5-15 sec) et consomme
  // 1 crédit. C'est important d'expliquer ce qui se passe à l'utilisateur,
  // sinon il croit que la page est cassée et reclique → double facturation.
  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#9b8cff' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerPatrimonial.initialLoading')}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{ color: '#a0b4d6 !important', mt: 1, maxWidth: 480 }}
          >
            {t('conseillerPatrimonial.initialLoadingHint')}
          </WTypography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(0,0,29,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* LAP-CP01 — Top bar responsive : sur mobile (xs/sm) on bascule les
            boutons en icon-only et on masque le sous-titre pour libérer la
            largeur, qui sinon est complètement bouffée par "Mon Conseiller
            Patrimonial" + les libellés "Télécharger le rapport" / "Masquer
            le chat". À partir de md on retrouve les boutons texte complets. */}
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/MonPatrimoine')}
            sx={{ color: '#dbe5f7', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerPatrimonial.pageTitle')}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{
                color: '#a0b4d6 !important',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {/* LAP-CP03/06 — Sous-titre dynamique basé sur lastUpdate +
                  indicateurs cache & théorique. */}
              {t('conseillerPatrimonial.lastUpdated', {
                date: formatRelativeI18n(lastUpdate, t),
              })}
              {wasFromCache === true && (
                <>
                  {' · '}
                  {t('conseillerPatrimonial.fromCache')}
                </>
              )}
              {advisorData?.userProfile?.isCashflowTheoretical && (
                <>
                  {' · '}
                  <Box
                    component="span"
                    sx={{ color: '#fbbf24 !important', fontWeight: 600 }}
                  >
                    {t('conseillerPatrimonial.theoreticalChip')}
                  </Box>
                </>
              )}
            </WTypography>
          </Box>

          {/* LAP-CP03 — Bouton refresh / régénérer (mobile + desktop, icon-only)
              Disponible à toutes les tailles d'écran car c'est une action clé. */}
          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#dbe5f7',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(219,229,247,0.4)' },
            }}
            aria-label={t('conseillerPatrimonial.regenerateAnalysis')}
            title={t('conseillerPatrimonial.regenerateAnalysis')}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          {/* Mobile : icon-only download */}
          <IconButton
            onClick={handleDownloadReport}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: '#dbe5f7',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            }}
            aria-label={t('conseillerPatrimonial.downloadReport')}
          >
            <DownloadIcon />
          </IconButton>

          {/* Mobile : icon-only chat toggle */}
          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#3b82f6' : 'transparent',
              color: chatOpen ? 'white' : '#dbe5f7',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: chatOpen ? '#2563eb' : 'rgba(255,255,255,0.06)',
              },
            }}
            aria-label={
              chatOpen
                ? t('conseillerPatrimonial.hideChat')
                : t('conseillerPatrimonial.openChat')
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          {/* Desktop : boutons texte complets */}
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              color: '#dbe5f7',
              textTransform: 'none',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            {t('conseillerPatrimonial.downloadReport')}
          </Button>
          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }
                : { color: '#dbe5f7', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerPatrimonial.hideChat')
              : t('conseillerPatrimonial.openChat')}
          </Button>
        </Container>
      </Box>

      {/* LAP-748 — Bandeau "Estimation théorique" : visible quand aucune
          opération bancaire n'a été importée. Les chiffres affichés viennent
          alors du budget de référence et des cibles de répartition d'épargne,
          pas de transactions réelles. Disparaît dès que l'utilisateur a
          importé des opérations (totalActifs > 0). */}
      {advisorData?.userProfile?.isCashflowTheoretical && (
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(251,191,36,0.10)',
              border: '1px solid rgba(251,191,36,0.30)',
            }}
          >
            <Box
              sx={{ fontSize: 20, lineHeight: 1, color: '#fbbf24', flexShrink: 0, mt: 0.2 }}
            >
              ⓘ
            </Box>
            <Box flex={1}>
              <WTypography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#fbbf24 !important' }}
              >
                {t('conseillerPatrimonial.theoreticalTitle')}
              </WTypography>
              <WTypography
                variant="body2"
                sx={{ color: '#fbd97d !important', mt: 0.5, lineHeight: 1.5 }}
              >
                {t('conseillerPatrimonial.theoreticalBody')}
              </WTypography>
              <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  onClick={() => navigate('/mescomptes')}
                  sx={{
                    bgcolor: 'rgba(251,191,36,0.18)',
                    color: '#fbd97d',
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(251,191,36,0.3)' },
                  }}
                >
                  {t('conseillerPatrimonial.connectAccounts')}
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate('/gestionoperations')}
                  sx={{
                    color: '#fbd97d',
                    textTransform: 'none',
                    border: '1px solid rgba(251,191,36,0.4)',
                    '&:hover': { bgcolor: 'rgba(251,191,36,0.1)' },
                  }}
                >
                  {t('conseillerPatrimonial.importOperations')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      )}

      {/* LAP-748 — Bandeau "Profil incomplet" : n'apparaît QUE si au moins un
          champ critique manque vraiment. Chaque CTA est conditionnel : si tu
          as rempli ton profil + intérêts, le CTA correspondant ne s'affiche
          pas. Si tout est rempli, aucun bandeau du tout. */}
      {advisorData?.completeness &&
        (!advisorData.completeness.hasBudget ||
          !advisorData.completeness.hasProfilInvestisseur ||
          !advisorData.completeness.hasPatrimoine ||
          !advisorData.completeness.hasObjectifs) && (
          <Container maxWidth="xl" sx={{ pt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: 'rgba(126,156,219,0.10)',
                border: '1px solid rgba(126,156,219,0.30)',
              }}
            >
              <Box
                sx={{ fontSize: 20, lineHeight: 1, color: '#7e9cdb', flexShrink: 0, mt: 0.2 }}
              >
                ⓘ
              </Box>
              <Box flex={1}>
                <WTypography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#a8c1f0 !important' }}
                >
                  {t('conseillerPatrimonial.completenessTitle')}
                </WTypography>
                <WTypography
                  variant="body2"
                  sx={{ color: '#c1d0eb !important', mt: 0.5, lineHeight: 1.5 }}
                >
                  {t('conseillerPatrimonial.completenessBody')}
                </WTypography>
                <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                  {!advisorData.completeness.hasBudget && (
                    <Button
                      size="small"
                      onClick={() => navigate('/Monbudget')}
                      sx={ctaButtonSx}
                    >
                      {t('conseillerPatrimonial.completeBudget')}
                    </Button>
                  )}
                  {!advisorData.completeness.hasPatrimoine && (
                    <Button
                      size="small"
                      onClick={() => navigate('/MonPatrimoine')}
                      sx={ctaButtonSx}
                    >
                      {t('conseillerPatrimonial.completePatrimoine')}
                    </Button>
                  )}
                  {!advisorData.completeness.hasProfilInvestisseur && (
                    <Button
                      size="small"
                      onClick={() => navigate('/MonPatrimoine')}
                      sx={ctaButtonSx}
                    >
                      {t('conseillerPatrimonial.completeProfilInvestisseur')}
                    </Button>
                  )}
                  {!advisorData.completeness.hasObjectifs && (
                    <Button
                      size="small"
                      onClick={() => navigate('/mesobjectifs')}
                      sx={ctaButtonSx}
                    >
                      {t('conseillerPatrimonial.completeObjectifs')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Container>
        )}
      <Container maxWidth="xl" sx={{ pt: 1 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            lg: chatOpen ? '2fr 1fr' : '1fr',
          }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <ScorePatrimonial
                  score={advisorData.patrimoineScore}
                  dimensions={advisorData.patrimoineScore.dimensions}
                  userFirstName={displayedFirstName}
                />
                <DiagnosticPanel diagnostic={advisorData.diagnostic} />
                <ActionsPrioritaires
                  actions={advisorData.actionsPrioritaires}
                  onCreateProject={handleCreateProject}
                  onSimulate={handleSimulate}
                />
                <SimulationCompare projection={advisorData.projection20ans} />
                <ProjetsCreables
                  projets={advisorData.projetsSugeres}
                  onCreate={handleCreateProjet}
                  onCreateObjectif={handleCreateObjectif}
                />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={buildInitialChatMessages(displayedFirstName, t)}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

/**
 * LAP-CP03 / LAP-i18n — Formate une date en relatif court ("il y a 5 min", "à
 * l'instant", "il y a 2 h"). On utilise les clés i18n `relativeTime.*` pour
 * supporter toutes les langues LIFE (FR/EN/DE/ES/IT/JA/ZH/AR), au lieu de
 * brancher en dur uniquement FR vs EN.
 */
function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then) // ms
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1) return t('conseillerPatrimonial.relativeTime.justNow')
  if (minutes < 60) return t('conseillerPatrimonial.relativeTime.minutesAgo', { n: minutes })
  if (hours < 24) return t('conseillerPatrimonial.relativeTime.hoursAgo', { n: hours })
  return t('conseillerPatrimonial.relativeTime.daysAgo', { n: days })
}

export default ConseillerPatrimonialPage
